import { Injectable } from '@angular/core';

import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { PouchDoc } from '@cf-shared/data-model/pouch';
import { combinedEntryForExercise, Log_Workout, Log_WorkoutExercise, Log_WorkoutSet } from '@cf-shared/data-model/log-workout';
import { AuthService } from 'services/auth.service';
import { LoggingService } from 'services/logging.service';
import { TransactionId } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { PlanMerge, WorkoutLoaderService } from 'services/workout-loader.service';
import { sessionSetsForExercise } from 'pages/workout-detail/workout-progression-decision';
import { ExerciseChangeAuthor, ExerciseChangeDoc } from '@cf-shared/data-model/exercise-change';

/** how many recent workouts are read; the same window the prescription cap reads (#5176) */
const RECENT_WORKOUTS_READ = 12;

/** one exercise's recent history, newest first, as the coach's page and the save need it */
export interface RecentExerciseHistory {
  /** the workouts that hold the exercise, newest first, each once */
  workouts: Log_Workout[];
  /** the exercise's sets in each of those workouts (for the best recent set) */
  sessionsNewestFirst: Log_WorkoutSet[][];
  /** the exercise's entry in each of those workouts (for the deload rule, which needs the day) */
  entriesNewestFirst: Log_WorkoutExercise[];
}

/**
 * The database side of a coach's save of one exercise (#6428). Both save routes call it: Edit
 * Workouts and the Training Overview (whose service is shared by the coach check-up's Training
 * tab). The rules themselves are pure, in `coach-exercise-change.ts`; this reads the history they
 * need and writes the record they produce. See `design-docs/coach-exercise-editing.md`.
 */
@Injectable({ providedIn: 'root' })
export class CoachExerciseChangeService {
  constructor(
    private userDb: UserDb,
    private workoutLoader: WorkoutLoaderService,
    private logging: LoggingService,
    private auth: AuthService,
  ) {}

  /**
   * The exercise's recent history. Throws `DatabaseUnavailableError` when it could not be read; a
   * caller must never read that as "no history", because the deload rule and the calculated
   * weights would then be worked out from nothing.
   */
  async readHistoryAsync(exerciseId: string, plan: FitnessPlan): Promise<RecentExerciseHistory> {
    const read = await this.userDb.getRecentWorkoutsForExerciseAsync(exerciseId, RECENT_WORKOUTS_READ);
    const workouts: Log_Workout[] = [];
    const sessionsNewestFirst: Log_WorkoutSet[][] = [];
    const entriesNewestFirst: Log_WorkoutExercise[] = [];
    // the view emits one row per exercise ENTRY, so a workout holding the exercise twice comes back twice
    const seen = new Set<string>();
    for (const workout of read) {
      if (!workout || seen.has(workout._id)) continue;
      seen.add(workout._id);
      const exercises = this.workoutLoader.getEffectiveExercises(workout, plan, PlanMerge.AsStored);
      const sets = sessionSetsForExercise(exercises, exerciseId);
      if (!sets.length) continue;
      workouts.push(workout);
      sessionsNewestFirst.push(sets);
      entriesNewestFirst.push(combinedEntryForExercise(exercises, exerciseId));
    }
    return { workouts, sessionsNewestFirst, entriesNewestFirst };
  }

  /** the signed-in person: a coach while managing a client (`auth.user` is still the coach), else the user themselves */
  author(): ExerciseChangeAuthor {
    const role = this.userDb.getCoachManagingClient() ? 'coach' : 'self';
    return { uid: this.auth.user?.uid ?? '', name: this.auth.user?.name ?? '', role };
  }

  /**
   * Writes the records of one save, after the plan and fitness state are saved. A failure is
   * reported and swallowed on purpose: the plan is the source of truth, and a missing record only
   * loses the history line and the client's note. Throwing here would tell the coach the save
   * failed when it did not.
   */
  async recordAsync(docs: readonly ExerciseChangeDoc[], transaction: TransactionId): Promise<void> {
    for (const doc of docs) {
      try {
        await this.userDb.database.createUniqueJournalEntryAsync(doc as PouchDoc, 'ExerciseChange', transaction);
      } catch (err) {
        this.logging.trackAbnormalException(err);
      }
    }
  }
}
